'use client'

import type {
  LadderPolicyChange,
  LadderPolicyData,
} from '@/components/molecules/constraint-ladder-policy/types'

import { MaturityOption } from '@moment/horizon-data-access/proposal'
import { BondType } from '@moment/trading-types/reference-data'
import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { ButtonMultiSelect } from '@/components/atomic/button-multi-select'
import { IconName } from '@/components/atomic/icon'
import Input from '@/components/atomic/input'
import { Select } from '@/components/atomic/select'
import ConstraintLadderPolicy from '@/components/molecules/constraint-ladder-policy'
import ModelConstraintCard from '@/components/molecules/model-constraint-card'
import ModelSectionCard from '@/components/molecules/model-section-card'
import { StateSelect } from '@/components/molecules/state-select'
import {
  TargetBand,
  type TargetBandState,
  TargetBandType,
} from '@/components/molecules/target-band'
import { useGetOrganizationProfileQuery } from '@/legacy-api/organizations-slice'
import {
  ALLOCATION_CONSTRAINT_FIELDS,
  DIVERSIFICATION_CONSTRAINT_FIELDS,
  type ModelBuilder,
  setModelField,
  TARGET_AVERAGE_CONSTRAINT_FIELDS,
  touchField,
} from '@/reducers/model-builder'
import { TargetBandMode } from '@/types/enums/target-band.mode'
import { ValueUnit } from '@/types/enums/value-unit'

import { DEFAULT_MODEL_BUILDER_VALUES } from './default-values'
import { useModelData } from './helpers'
import {
  getFieldsToResetOnPreferenceChange,
  MunicipalStatePreference,
} from './municipal-state-preference'
import {
  ModelAssetClassContainer,
  ModelAssetClassWidth,
  ModelLadderPolicyContainer,
  ModelSetupTabContainer,
  ModelStateAllocationContainer,
  ModelStateAllocationWidth,
  ModelStateOptionsContainer,
} from './styles'

enum ModelBuilderMode {
  Creating = 'creating',
  Editing = 'editing',
}

// Types
interface SelectOption {
  label: string
  value: string
}

// Constants
const ASSET_CLASS_OPTIONS: SelectOption[] = [
  { label: 'Treasury', value: BondType.Treasury },
  { label: 'Corporate', value: BondType.Corporate },
  { label: 'Municipal', value: BondType.Municipal },
  { label: 'CD', value: BondType.CD },
  { label: 'Agency', value: BondType.Agency },
]

const MUNICIPAL_STATE_OPTIONS: SelectOption[] = [
  { label: 'National', value: MunicipalStatePreference.National },
  { label: 'State-Preferred', value: MunicipalStatePreference.StatePreferred },
  { label: 'State-Focused', value: MunicipalStatePreference.StateFocused },
]

const MODEL_SECTIONS = {
  name: {
    title: 'Model name',
    description: 'Set the name of this model',
    icon: IconName.CubeIcon,
  },
  description: {
    title: 'Model description',
    description: 'Add an optional description for this model',
    icon: IconName.DocumentTextIcon,
  },
  cash: {
    title: 'Cash amount',
    description: 'Specify the cash amount available when previewing the model',
    icon: IconName.CurrencyDollarIcon,
  },
  assetClass: {
    title: 'Asset class',
    description: 'Select the asset class for this model',
    icon: IconName.CircleStackIcon,
  },
  ladderPolicy: {
    title: 'Ladder policy',
    description: 'Configure the ladder policy for this model',
    icon: IconName.ChartBarSquareIcon,
  },
  organizationSharing: {
    title: 'Organization sharing',
    description: 'Configure which organizations can access this model',
    icon: IconName.ShareIcon,
  },
} as const

export const sliderConfigs = {
  rangeInPeriods: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.rangeInPeriods as number[],
    min: 0,
    max: 30,
    step: 1,
  },
}

const useModelActions = () => {
  const dispatch = useDispatch()

  const handleFieldChange = useCallback(
    <T extends keyof ModelBuilder>(field: T, value: ModelBuilder[T]) => {
      dispatch(setModelField({ field, value }))
    },
    [dispatch],
  )

  const handleBlur = useCallback(
    (field: keyof ModelBuilder) => (_e: React.FocusEvent<HTMLInputElement>) => {
      dispatch(touchField(field))
    },
    [dispatch],
  )

  return { handleFieldChange, handleBlur }
}

const stateFieldsToTargetBand = (model: ModelBuilder): TargetBandState => {
  return {
    target: model?.stateTargetWeight?.toString() ?? '',
    min: model?.stateMinWeight?.toString() ?? '',
    max: model?.stateMaxWeight?.toString() ?? '',
    buffer: model?.stateBuffer?.toString() ?? '',
    mode: TargetBandMode.Buffer, // Always use Buffer mode for 20% auto-calculation
    unit: ValueUnit.Percentage,
  }
}

const targetBandToStateFields = (state: TargetBandState) => {
  return {
    stateTargetWeight: state.target ? parseFloat(state.target) : null,
    stateMinWeight: state.min ? parseFloat(state.min) : null,
    stateMaxWeight: state.max ? parseFloat(state.max) : null,
    stateBuffer: state.buffer ? parseFloat(state.buffer) : null,
  }
}

const useStateAllocationHandlers = (
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
  model: ModelBuilder,
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  handleFieldChange: any,
) => {
  const handleStateAllocationChange = useCallback(
    (state: TargetBandState) => {
      const fields = targetBandToStateFields(state)
      Object.entries(fields).forEach(([field, value]) => {
        handleFieldChange(field, value)
      })
    },
    [handleFieldChange],
  )

  return { handleStateAllocationChange }
}

function ModelSetupTab({ mode }: { mode: ModelBuilderMode }) {
  const model = useModelData()
  const { handleFieldChange, handleBlur } = useModelActions()

  const {
    name,
    description,
    cash,
    assetClasses,
    sharing_organization_id,
    municipalStatePreference,
    municipalState,
    rangeInPeriods,
    bucketWidth,
  } = model || {}

  // Organization data for sharing dropdown
  const { data: organizationProfile } = useGetOrganizationProfileQuery()

  // Organization dropdown options
  const organizationOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = []
    if (organizationProfile) {
      options.push({
        label: `${organizationProfile.name} (my org)`,
        value: organizationProfile.id,
      })

      // Add introducing brokers
      organizationProfile.introducing_brokers?.forEach((ib) => {
        options.push({
          label: ib.name,
          value: ib.id,
        })
      })
    }

    return options
  }, [organizationProfile])

  const { handleStateAllocationChange } = useStateAllocationHandlers(
    model,
    handleFieldChange,
  )

  // Memoized handlers

  const handleAssetClassChange = useCallback(
    (values: string[]) => {
      handleFieldChange('assetClasses', values as BondType[])
      if (!values.includes(BondType.Municipal)) {
        const municipalFieldsToReset: (keyof ModelBuilder)[] = [
          'municipalStatePreference',
          'municipalState',
          'stateMinWeight',
          'stateTargetWeight',
          'stateMaxWeight',
          'stateBuffer',
        ]
        // biome-ignore lint/suspicious/useIterableCallbackReturn: Bulk suppression as part of biome migration
        municipalFieldsToReset.forEach((field) =>
          handleFieldChange(field, null),
        )
      }

      const constraintFieldsToReset = [
        ...ALLOCATION_CONSTRAINT_FIELDS,
        ...DIVERSIFICATION_CONSTRAINT_FIELDS,
        ...TARGET_AVERAGE_CONSTRAINT_FIELDS,
      ]

      constraintFieldsToReset.forEach((field) => {
        const defaultValue = DEFAULT_MODEL_BUILDER_VALUES[field]
        handleFieldChange(field, defaultValue)
      })
    },
    [handleFieldChange],
  )

  const handleMunicipalStatePreferenceChange = useCallback(
    (values: string[]) => {
      const selectedPreference = values[0] as MunicipalStatePreference
      handleFieldChange('municipalStatePreference', selectedPreference)

      const fieldsToReset =
        getFieldsToResetOnPreferenceChange(selectedPreference)
      // biome-ignore lint/suspicious/useIterableCallbackReturn: Bulk suppression as part of biome migration
      fieldsToReset.forEach((field) => handleFieldChange(field, null))
    },
    [handleFieldChange],
  )

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFieldChange('name', e.target.value)
    },
    [handleFieldChange],
  )

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFieldChange('description', e.target.value)
    },
    [handleFieldChange],
  )

  const handleCashChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^\d]/g, '')
      handleFieldChange('cash', value === '' ? null : parseInt(value, 10))
    },
    [handleFieldChange],
  )

  const handleStateChange = useCallback(
    (value: string) => {
      handleFieldChange('municipalState', value)
    },
    [handleFieldChange],
  )

  const handleOrganizationSharingChange = useCallback(
    (value: string) => {
      handleFieldChange('sharing_organization_id', value)
    },
    [handleFieldChange],
  )

  // Memoized derived values
  const showMunicipalOptions = useMemo(
    () => assetClasses?.includes(BondType.Municipal),
    [assetClasses],
  )

  const isStateSelectDisabled = useMemo(
    () =>
      !municipalStatePreference ||
      municipalStatePreference === MunicipalStatePreference.National,
    [municipalStatePreference],
  )

  const isStatePreferred = useMemo(
    () => municipalStatePreference === MunicipalStatePreference.StatePreferred,
    [municipalStatePreference],
  )

  const isEditingExisting = useMemo(
    () => mode === ModelBuilderMode.Editing,
    [mode],
  )

  return (
    <ModelSetupTabContainer>
      <ModelSectionCard
        required
        title={MODEL_SECTIONS.name.title}
        description={MODEL_SECTIONS.name.description}
        icon={MODEL_SECTIONS.name.icon}
        iconColor="text-moment-blue-grey-800 dark:text-moment-blue-grey-400"
        iconBackgroundColor="bg-moment-blue-grey-100 dark:bg-moment-blue-grey-900"
        headerContent={
          <Input
            placeholder="Enter name"
            width="full"
            size="large"
            value={name}
            onChange={handleNameChange}
            onBlur={handleBlur('name')}
          />
        }
      />

      <ModelSectionCard
        title={MODEL_SECTIONS.description.title}
        description={MODEL_SECTIONS.description.description}
        icon={MODEL_SECTIONS.description.icon}
        iconColor="text-moment-blue-grey-800 dark:text-moment-blue-grey-400"
        iconBackgroundColor="bg-moment-blue-grey-100 dark:bg-moment-blue-grey-900"
        headerContent={
          <Input
            placeholder="Enter description"
            width="full"
            size="large"
            value={description ?? ''}
            onChange={handleDescriptionChange}
            onBlur={handleBlur('description')}
          />
        }
      />

      <ModelSectionCard
        title={MODEL_SECTIONS.cash.title}
        description={MODEL_SECTIONS.cash.description}
        icon={MODEL_SECTIONS.cash.icon}
        iconColor="text-moment-orange-800 dark:text-moment-orange-400"
        iconBackgroundColor="bg-moment-orange-100 dark:bg-moment-orange-900"
        headerContent={
          <Input
            placeholder="Enter cash amount"
            width="full"
            size="large"
            value={cash?.toLocaleString() ?? ''}
            onChange={handleCashChange}
            onBlur={handleBlur('cash')}
            slotProps={{
              icon: <span className="text-secondary">$</span>,
              side: 'left',
            }}
          />
        }
      />

      <ModelSectionCard
        required
        title={MODEL_SECTIONS.assetClass.title}
        description={MODEL_SECTIONS.assetClass.description}
        icon={MODEL_SECTIONS.assetClass.icon}
        iconColor="text-moment-green-800 dark:text-moment-green-600"
        iconBackgroundColor="bg-moment-green-100 dark:bg-moment-green-1000"
      >
        <ModelAssetClassContainer>
          <ModelAssetClassWidth>
            <ModelConstraintCard title="Asset Class">
              <ButtonMultiSelect
                key={`asset-class-${name}-${assetClasses.join('-')}`}
                options={ASSET_CLASS_OPTIONS}
                value={assetClasses}
                onValueChange={handleAssetClassChange}
              />
            </ModelConstraintCard>
          </ModelAssetClassWidth>
          {showMunicipalOptions && (
            <ModelAssetClassWidth>
              <ModelConstraintCard title="Municipal state">
                <ModelStateOptionsContainer>
                  <ButtonMultiSelect
                    columns={3}
                    isMultiSelect={false}
                    options={MUNICIPAL_STATE_OPTIONS}
                    value={
                      municipalStatePreference ? [municipalStatePreference] : []
                    }
                    onValueChange={handleMunicipalStatePreferenceChange}
                  />
                  <StateSelect
                    value={municipalState ?? ''}
                    onValueChange={handleStateChange}
                    disabled={isStateSelectDisabled}
                  />
                </ModelStateOptionsContainer>
              </ModelConstraintCard>
            </ModelAssetClassWidth>
          )}
        </ModelAssetClassContainer>
        {isStatePreferred && (
          <ModelStateAllocationContainer>
            <ModelStateAllocationWidth>
              <ModelConstraintCard title="State Allocation">
                <TargetBand
                  initial={stateFieldsToTargetBand(model)}
                  onChange={handleStateAllocationChange}
                  unit={ValueUnit.Percentage}
                  type={TargetBandType.Numeric}
                />
              </ModelConstraintCard>
            </ModelStateAllocationWidth>
          </ModelStateAllocationContainer>
        )}
      </ModelSectionCard>

      <ModelSectionCard
        title={MODEL_SECTIONS.ladderPolicy.title}
        description={MODEL_SECTIONS.ladderPolicy.description}
        icon={MODEL_SECTIONS.ladderPolicy.icon}
        iconColor="text-moment-purple-700 dark:text-moment-purple-200"
        iconBackgroundColor="bg-moment-purple-100 dark:bg-moment-purple-900"
        toggleable
        isToggleChecked={model?.ladderPolicyEnabled ?? false}
        onToggleChange={(checked) => {
          handleFieldChange('ladderPolicyEnabled', checked)
          if (!checked) {
            // Disable ladder policy by setting rangeinYears to null
            handleFieldChange('rangeInPeriods', null)
            // Clear maturityOption when disabling ladder policy
            handleFieldChange('maturityOption', MaturityOption.maturity_date)
          } else {
            // Restore default values when toggle is checked
            handleFieldChange(
              'rangeInPeriods',
              DEFAULT_MODEL_BUILDER_VALUES.rangeInPeriods,
            )
            handleFieldChange(
              'bucketWeightBuffer',
              DEFAULT_MODEL_BUILDER_VALUES.bucketWeightBuffer,
            )
            handleFieldChange(
              'useCallAdjustedMaturities',
              DEFAULT_MODEL_BUILDER_VALUES.useCallAdjustedMaturities,
            )
            handleFieldChange(
              'maturityOption',
              DEFAULT_MODEL_BUILDER_VALUES.maturityOption,
            )
          }
        }}
      >
        <ModelLadderPolicyContainer>
          <ConstraintLadderPolicy
            data={{
              period_length: bucketWidth || 12,
              start_period: rangeInPeriods?.[0] || 0,
              end_period: rangeInPeriods?.[1] || 0,
              weight_tolerance: model?.bucketWeightBuffer
                ? model.bucketWeightBuffer / 100
                : 0,
              use_call_adjusted_maturity:
                model?.useCallAdjustedMaturities || false,
              maturity_option:
                model?.maturityOption || MaturityOption.maturity_date,
            }}
            onChange={(data: LadderPolicyData, change?: LadderPolicyChange) => {
              handleFieldChange('bucketWidth', data.period_length)
              handleFieldChange('rangeInPeriods', [
                data.start_period,
                data.end_period,
              ])
              // Only an edit to the tolerance itself replaces a dollar tolerance.
              if (change?.weightToleranceEdited) {
                handleFieldChange(
                  'bucketWeightBuffer',
                  data.weight_tolerance * 100,
                )
                handleFieldChange('ladderMarketValueTolerance', null)
              }
              // keep for now, but should be refactored in the future
              if (data.maturity_option !== undefined) {
                handleFieldChange('maturityOption', data.maturity_option)
                handleFieldChange(
                  'useCallAdjustedMaturities',
                  data.maturity_option === MaturityOption.workout_date,
                )
              } else {
                handleFieldChange(
                  'useCallAdjustedMaturities',
                  data.use_call_adjusted_maturity ?? false,
                )
              }
            }}
            disabled={false}
          />
        </ModelLadderPolicyContainer>
      </ModelSectionCard>

      <ModelSectionCard
        title={MODEL_SECTIONS.organizationSharing.title}
        description={MODEL_SECTIONS.organizationSharing.description}
        icon={MODEL_SECTIONS.organizationSharing.icon}
        iconColor="text-moment-blue-700 dark:text-moment-blue-300"
        iconBackgroundColor="bg-moment-blue-100 dark:bg-moment-blue-900"
        headerContent={
          <div className="flex items-center gap-3">
            <div className="min-w-[200px]">
              <Select
                options={organizationOptions}
                value={
                  sharing_organization_id || organizationOptions[0]?.value || ''
                }
                onValueChange={handleOrganizationSharingChange}
                disabled={isEditingExisting}
                placeholder="Select sharing organization"
              />
            </div>
          </div>
        }
      />
    </ModelSetupTabContainer>
  )
}

// Dead UI retained for live constants in this file; unexported for knip.
void ModelSetupTab
